import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api, { setAccessToken, onAuthFailed } from './api';

const AuthCtx = createContext(null);

const SCOPE = { global: 3, organization: 2, unit: 1, own: 0 };

export function can(permissions, resource, action, minScope = null) {
  if (!permissions?.length) return false;
  if (permissions.includes('*:*:*')) return true;
  const r = resource.toLowerCase(), a = action.toLowerCase();
  for (const p of permissions) {
    if (typeof p !== 'string') continue;
    const parts = p.split(':');
    if (parts.length !== 3) continue;
    const [pr, ps, pa] = parts.map(s => s.toLowerCase());
    if (pr !== r && pr !== '*') continue;
    if (pa !== a && pa !== '*') continue;
    const lvl = SCOPE[ps];
    if (lvl === undefined) continue;
    if (minScope === null) return true;
    const need = SCOPE[minScope.toLowerCase()];
    if (need === undefined) continue;
    if (lvl >= need) return true;
  }
  return false;
}

export function isSuperAdmin(permissions) {
  return Array.isArray(permissions) && permissions.includes('*:*:*');
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    const { data } = await api.get('/auth/me');
    setUser(data.user);
    setPermissions(data.permissions || []);
  }, []);

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.post('/auth/refresh');
      setAccessToken(data.access_token);
      await loadMe();
    } catch {
      setAccessToken(null);
      setUser(null);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, [loadMe]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // When the API client gives up (refresh failed), clear React state — ProtectedRoute
  // will route to /login via react-router instead of a hard reload.
  useEffect(() => {
    onAuthFailed(() => {
      setAccessToken(null);
      setUser(null);
      setPermissions([]);
    });
  }, []);

  // Imperative session check — used by both the heartbeat and the per-route hook.
  const validateSession = useCallback(async () => {
    if (!user) return;
    try { await api.get('/auth/me'); }
    catch (err) {
      if (err.response?.status === 401) {
        setAccessToken(null); setUser(null); setPermissions([]);
      }
    }
  }, [user]);

  // Heartbeat: every 30 s, on tab focus, and on visibility-change.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      try { await api.get('/auth/me'); }
      catch (err) {
        if (cancelled) return;
        if (err.response?.status === 401) {
          setAccessToken(null); setUser(null); setPermissions([]);
        }
      }
    };
    const interval = setInterval(check, 30_000);
    const onFocus = () => check();
    const onVis = () => { if (!document.hidden) check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user]);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.stage === 'mfa') return { stage: 'mfa', mfaChallengeToken: data.mfa_challenge_token };
    setAccessToken(data.access_token);
    setUser(data.user);
    await loadMe();
    return { stage: 'complete', user: data.user, mustChangePassword: data.must_change_password };
  }, [loadMe]);

  const verifyMfa = useCallback(async ({ challengeToken, totpToken, recoveryCode }) => {
    const { data } = await api.post('/auth/mfa-verify', {
      challenge_token: challengeToken,
      totp_token: totpToken,
      recovery_code: recoveryCode
    });
    setAccessToken(data.access_token);
    setUser(data.user);
    await loadMe();
    return { user: data.user, mustChangePassword: data.must_change_password };
  }, [loadMe]);

  const logout = useCallback(async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    setPermissions([]);
  }, []);

  const helpers = useMemo(() => ({
    can: (resource, action, minScope = 'global') => can(permissions, resource, action, minScope),
    isSuperAdmin: () => isSuperAdmin(permissions)
  }), [permissions]);

  return (
    <AuthCtx.Provider value={{ user, permissions, loading, login, verifyMfa, logout, refreshMe: loadMe, validateSession, ...helpers }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
